import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  CardHeader,
  CardActions
} from '@material-ui/core'
import { ArrowBackIos, ArrowForwardIos } from '@material-ui/icons'
import HorizontalNewsCardSkeleton from 'src/components/Skeleton/News/HorizontalNewsCardSkeleton/HorizontalNewsCardSkeleton'
import getCategoryRouteByCategoryId from 'src/utils/News/getCategoryRouteByCategoryId'
import toSingularCategory from 'src/utils/News/toSingularCategory'
import { stripHtml } from 'src/utils/Html/stripHtml'
import { useStyles } from './NewsMostRecentCarousel.styles'

import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'
import 'swiper/components/pagination/pagination.min.css'

type NewsMostRecentCarouselProps = {
  items: any[]
  categories: any[]
}

const NewsMostRecentCarousel = ({
  items,
  categories
}: NewsMostRecentCarouselProps) => {
  const { t } = useTranslation()
  const [swiperInstance, setSwiperInstance] = useState<any>(null)
  const [newsMostRecent, setNewsMostRecent] = useState<any>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setInit] = useState(false)
  const classes = useStyles()

  useEffect(() => {
    if (swiperInstance) {
      swiperInstance.update()
      setInit((prev) => !prev)
    }
  }, [newsMostRecent, swiperInstance])

  useEffect(() => {
    if (items.length > 0) {
      setNewsMostRecent(items.slice(0, 3))
    }
  }, [items])

  return (
    <Box className={classes.mostRecentCarousel}>
      <IconButton
        aria-label={t('arial-label.previous-slide')}
        className={classes.mostRecentNavButton}
        onClick={() => swiperInstance?.slidePrev()}
        disabled={swiperInstance?.isBeginning}
      >
        <ArrowBackIos style={{ marginLeft: '4px' }} />
      </IconButton>
      <Swiper
        observer={true}
        observeParents={true}
        slidesPerView={1}
        centerInsufficientSlides={true}
        watchOverflow={false}
        threshold={5}
        spaceBetween={16}
        className={classes.mostRecentSwiperContainer}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper)
          setInit(true)
        }}
        onInit={(swiper) => {
          setSwiperInstance(swiper)
        }}
        onSlideChange={() => setInit((prev) => !prev)}
        onTransitionEnd={() => setInit((prev) => !prev)}
        onActiveIndexChange={() => setInit((prev) => !prev)}
        pagination={{ clickable: true }}
        breakpoints={{
          960: { slidesPerView: 1 }
        }}
      >
        {newsMostRecent.length > 0 ? (
          newsMostRecent.map((data: any, index: number) => {
            const category = getCategoryRouteByCategoryId(
              data.newsCategoriesId,
              categories
            )
            return (
              <SwiperSlide key={index}>
                <Card className={classes.card}>
                  <CardActionArea
                    component={RouterLink}
                    to={`/news/${category}/${data.id}`}
                    className={classes.cardActionArea}
                  >
                    <CardMedia
                      component="img"
                      image={data.path}
                      title={data.label}
                      className={classes.moreRecentImgContainer}
                    />
                    <CardContent
                      className={classes.moreRecentInformationContainer}
                    >
                      <Typography variant="body2" className={classes.category}>
                        {t(`news.category.${toSingularCategory(category)}`)}
                      </Typography>
                      <CardHeader
                        variant="body2"
                        title={data.title}
                        className={classes.moreRecentTitle}
                      />
                      <Typography
                        variant="h6"
                        className={classes.moreRecentText}
                      >
                        {stripHtml(data.text)}
                      </Typography>
                      <CardActions className={classes.actionReadMore}>
                        <Button className={classes.actionsButton}>
                          <Typography variant="body1">
                            {t('button.read-more')}
                          </Typography>
                        </Button>
                      </CardActions>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </SwiperSlide>
            )
          })
        ) : (
          <SwiperSlide>
            <HorizontalNewsCardSkeleton />
          </SwiperSlide>
        )}
      </Swiper>
      <IconButton
        aria-label={t('arial-label.next-slide')}
        className={classes.mostRecentNavButton}
        onClick={() => swiperInstance?.slideNext()}
        disabled={swiperInstance?.isEnd}
      >
        <ArrowForwardIos />
      </IconButton>
    </Box>
  )
}

export default NewsMostRecentCarousel
